import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import createAxios from "@/lib/axios";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableHeader,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2Icon } from "lucide-react";
import { Uploader } from "./Uploader";
import { FilePond } from "react-filepond";
import { deleteObject, ref } from "firebase/storage";
import { storage } from "@/lib/firebase";
import { useRef } from "react";
import { usePeriodicRefresh } from "@/hooks/usePeriodicRefresh";

type EvaluationSet = {
  id: string;
  name: string;
  progress: {
    finished: number;
    total: number;
    error: number;
  };
};

type AnswerSheet = {
  id: string;
  name: string;
  file_path: string;
  progress: {
    finished: number;
    total: number;
    error: number;
  };
  marks_obtained: number;
};

type EvaluationData = {
  job_info: EvaluationSet;
  answer_sheets: AnswerSheet[];
};

function ViewEvaluation() {
  usePeriodicRefresh(10_000);
  const { evaluationData, userId, id } = Route.useLoaderData();
  const evaluationSet = evaluationData.job_info;
  const answerSheets = evaluationData.answer_sheets;

  const pondRef = useRef<FilePond>(null);

  const progressPercentage =
    evaluationSet && "progress" in evaluationSet
      ? Math.round(
          (evaluationSet.progress.finished / evaluationSet.progress.total) *
            100,
        )
      : 0;

  const navigate = useNavigate();

  const handleDelete = async (scriptId: string, filePath: string) => {
    try {
      const fileRef = ref(storage, `gs://input-aae2e1fa3a5548a/${filePath}`);
      await deleteObject(fileRef);
      const client = await createAxios();
      await client.Delete(
        `${import.meta.env.VITE_BACKEND_URL}/answer-sheet/${scriptId}/`,
      );
      toast.success(`File removed successfully`);
      navigate({ to: "/dashboard/evaluate/answer-script/$id", params: { id } });
    } catch (error) {
      toast.error(`Failed to delete file`);
      throw error;
    }
  };

  return (
    <div className="light flex h-full min-h-screen w-full flex-col">
      <Card className="m-4 flex-1">
        <CardHeader>
          <CardTitle>Review Evaluation: {evaluationSet.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between gap-2">
              {[
                {
                  name: "Average Marks",
                  value:
                    answerSheets.reduce(
                      (sheet, sum) => sheet + sum.marks_obtained,
                      0,
                    ) / answerSheets.length || 0,
                },
                {
                  name: "Progress",
                  value: evaluationSet.progress.finished,
                },
                { name: "Errors", value: evaluationSet.progress.error },
                { name: "Total", value: evaluationSet.progress.total },
              ].map(({ name, value }) => (
                <div key={name} className="flex flex-col items-start gap-2">
                  <p className="text-sm font-bold">{name}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              ))}
            </div>
            <div className="gap-2 space-y-2">
              <Progress value={progressPercentage} className="w-full" />
              <div className="text-sm text-muted-foreground">
                <span>Progress: </span>
                {evaluationSet.progress.finished} of{" "}
                {evaluationSet.progress.total} completed
                {evaluationSet.progress.error > 0 &&
                  ` (${evaluationSet.progress.error} errors)`}
              </div>
            </div>
            <div>
              <Table className="border-2">
                <TableHeader>
                  <TableRow>
                    <TableHead>Answer Sheet</TableHead>
                    <TableHead className="w-36">Obtained Marks</TableHead>
                    <TableHead className="w-96">Progress</TableHead>
                    <TableHead className="w-44">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableFooter>
                  {answerSheets &&
                    answerSheets.map((answerSheet: AnswerSheet) => (
                      <TableRow key={answerSheet.id}>
                        <TableCell>
                          <p>{answerSheet.name}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-2xl font-bold">
                            {answerSheet.marks_obtained}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="gap-2 space-y-2">
                            <Progress
                              value={Math.round(
                                (answerSheet.progress.finished /
                                  answerSheet.progress.total) *
                                  100,
                              )}
                              className="w-full"
                            />
                            <div className="text-xs text-muted-foreground">
                              {answerSheet?.progress?.finished || 0} of{" "}
                              {answerSheet?.progress?.total || 0} completed
                              {(answerSheet?.progress?.error || 0) > 0 &&
                                ` (${answerSheet.progress.error} errors)`}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="flex gap-2">
                          <Button
                            variant="outline2"
                            onClick={() =>
                              navigate({
                                to: `/dashboard/evaluate/answer-script/${answerSheet.id}`,
                              })
                            }
                          >
                            View
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() =>
                              handleDelete(
                                answerSheet.id,
                                answerSheet.file_path,
                              )
                            }
                          >
                            <Trash2Icon className="size-4"></Trash2Icon>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableFooter>
              </Table>
            </div>
            <div className="hidden">
              <FilePond
                ref={pondRef}
                allowMultiple={true}
                maxFiles={1024}
                server={{
                  revert: async (uniqueFileId, load) => {
                    try {
                      const fileRef = ref(
                        storage,
                        `gs://input-aae2e1fa3a5548a/${userId}/${uniqueFileId}`,
                      );
                      console.log("Removing file", uniqueFileId);
                      await deleteObject(fileRef);
                      load();
                    } catch (err) {
                      toast.error("Error reverting file");
                      throw err;
                    }
                  },
                }}
              />
            </div>
            {userId && <Uploader userId={userId} jobId={id} />}{" "}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

async function GetEvaluationDataById(id: string): Promise<EvaluationData> {
  const client = await createAxios();
  const { data } = await client.get<EvaluationData>(
    `${import.meta.env.VITE_BACKEND_URL}/evaluation/${id}`,
  );
  return data;
}

export const Route = createFileRoute("/dashboard/evaluate/$id")({
  component: ViewEvaluation,
  loader: async ({ params: { id }, context }) => {
    const evaluationData = await GetEvaluationDataById(id);
    const user = await context.auth.getUser();

    return {
      id,
      evaluationData,
      userId: user!.uid,
    };
  },
});
